import {
	getTableAllSelected,
	getTableAnySelected,
	getTableExcludedSelection,
	getTableSelectedIds,
	getTableSelection,
} from '@dealstack/frontend/shared/components/Table/MantineTable/table-utils';
import { TableSorting } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { RecordAny } from '@dealstack/shared/lib/common/common';
import { Row, Table } from '@tanstack/react-table';
import { getTableMeta } from '@dealstack/frontend/shared/components/Table/MantineTable/types';

export enum BulkOperationType {
	selected = 'selected',
	filtered = 'filtered',
}

export interface BulkOperationParams<
	Row extends RecordAny,
	/** What the backend expects (e.g. `TransactionFilterDto`) */
	FilterDto extends RecordAny,
	/** What you actually attach in React code – defaults to the DTO  */
	UICargo = FilterDto,
> {
	operationType?: BulkOperationType;
	selectedEntityId?: string;
	table?: Table<Row>;

	dataOperations?: UICargo;

	personId?: string;
	ids?: string[];
	investmentWorkflowIncluded?: boolean;
	tsaCount?: number;
	// CAP-1976: the selection contains at least one leg of a legal/beneficial interest pair —
	// bulk deletes/restores cascade to the missing sibling legs server-side, so the confirm copy
	// must say so. Like the TSA detection above, undetectable under greedy select-all.
	pairedLegsIncluded?: boolean;
	excludedIds?: string[];
}

export function getBulkOperationParams<T extends RecordAny, F extends RecordAny, D>(
	params: BulkOperationParams<T, F, D>
): BulkOperationParams<T, F, D> {
	const { operationType, selectedEntityId, table, dataOperations } = params;
	const baseParams = {
		personId: selectedEntityId,
		ids: [],
		investmentWorkflowIncluded: false,
		tsaCount: 0,
		pairedLegsIncluded: false,
	};

	if (!table) {
		return baseParams;
	}

	const { manualPagination, greedySelecting } = getTableMeta(table);

	let ids: string[] = [];
	let excludedIds: string[] = [];
	let tsaIds: Row<T>[] = [];
	let pairedLegRows: Row<T>[] = [];

	if (manualPagination && greedySelecting) {
		ids = [];
		excludedIds = getTableSelectedIds(table);
		tsaIds = [];
	} else {
		ids = getTableSelectedIds(table);
		excludedIds = getTableExcludedSelection(table);
		tsaIds = getTableSelection(table).filter(
			({ original }) =>
				original.investmentWorkflowId != null ||
				original.sourcePgTsaId != null ||
				original.destinationPgTsaId != null
		);
		pairedLegRows = getTableSelection(table).filter(
			({ original }) => original.interestPairId != null
		);
	}

	switch (operationType) {
		case BulkOperationType.selected:
			return {
				...baseParams,
				ids,
				investmentWorkflowIncluded: tsaIds.length > 0,
				tsaCount: tsaIds.length,
				pairedLegsIncluded: pairedLegRows.length > 0,
				operationType: BulkOperationType.selected,
				selectedEntityId,
				dataOperations,
			};
		case BulkOperationType.filtered: {
			return {
				...baseParams,
				excludedIds,
				dataOperations,
				investmentWorkflowIncluded: tsaIds.length > 0,
				tsaCount: tsaIds.length,
				pairedLegsIncluded: pairedLegRows.length > 0,
				operationType: BulkOperationType.filtered,
				selectedEntityId,
			};
		}
		default:
			return baseParams as BulkOperationParams<T, F, D>;
	}
}

export const getOperationType = <T extends RecordAny>(table: Table<T>) => {
	const isAnySelected = getTableAnySelected(table);
	const isAllSelected = getTableAllSelected(table);

	const { manualPagination, greedySelecting } = getTableMeta(table);
	const isGreedySelection = greedySelecting && manualPagination;

	return isAnySelected && !isAllSelected && !isGreedySelection
		? BulkOperationType.selected
		: BulkOperationType.filtered;
};

/**
 * Get the current sorting from table state.
 * Falls back to dataOperations sortBy if table sorting is empty.
 *
 * @param table - TanStack table instance
 * @param dataOperations - Data operations from the hook (may have stale sortBy)
 * @returns Current sorting array
 */
export function getCurrentSorting<T, D extends { sortBy?: TableSorting[] }>(
	table: Table<T> | undefined,
	dataOperations: D | undefined
): TableSorting[] | undefined {
	if (!table) {
		return dataOperations?.sortBy;
	}

	const tableSorting = table.getState().sorting || [];

	// If table has sorting, use that (it's always current)
	if (tableSorting.length > 0) {
		return tableSorting.map((s) => ({
			key: s.id,
			desc: s.desc ?? false,
		}));
	}

	// Otherwise fall back to dataOperations sortBy
	return dataOperations?.sortBy;
}
